import React, { useRef } from 'react';
import { Marker, Popup, Polygon } from 'react-leaflet';
import L from 'leaflet';

// Custom fire icon based on alert level with pin marker style
const getFireIcon = (alertLevel, size = 'medium') => {
  const colors = {
    critical: '#dc2626',  // Red
    warning: '#ea580c',   // Orange
    monitor: '#eab308'    // Yellow
  };

  const sizes = {
    small: 24,
    medium: 32,
    large: 40
  };

  const iconHeight = sizes[size] || sizes.medium;
  const iconWidth = iconHeight * 0.7;
  const color = colors[alertLevel] || colors.monitor;

  // SVG pin marker (teardrop shape with inner circle)
  const pinSvg = `
    <svg width="${iconWidth}" height="${iconHeight}" viewBox="0 0 24 36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 0C5.4 0 0 5.4 0 12c0 7.2 12 24 12 24s12-16.8 12-24c0-6.6-5.4-12-12-12z"
            fill="${color}"
            stroke="#ffffff"
            stroke-width="2"/>
      <circle cx="12" cy="11" r="5" fill="#ffffff"/>
    </svg>
  `;

  return L.divIcon({
    html: `
      <div style="
        width: ${iconWidth}px;
        height: ${iconHeight}px;
        display: flex;
        align-items: flex-start;
        justify-content: center;
        filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));
      ">${pinSvg}</div>
    `,
    className: 'wildfire-marker',
    iconSize: [iconWidth, iconHeight],
    iconAnchor: [iconWidth / 2, iconHeight],  // Anchor at bottom center of pin
    popupAnchor: [0, -iconHeight]
  });
};

// Facility/site marker (blue pin)
export const getFacilityIcon = () => {
  const iconHeight = 36;
  const iconWidth = iconHeight * 0.7;
  const color = '#2563eb';  // Blue

  const pinSvg = `
    <svg width="${iconWidth}" height="${iconHeight}" viewBox="0 0 24 36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 0C5.4 0 0 5.4 0 12c0 7.2 12 24 12 24s12-16.8 12-24c0-6.6-5.4-12-12-12z"
            fill="${color}"
            stroke="#ffffff"
            stroke-width="2"/>
      <circle cx="12" cy="11" r="5" fill="#ffffff"/>
    </svg>
  `;

  return L.divIcon({
    html: `
      <div style="
        width: ${iconWidth}px;
        height: ${iconHeight}px;
        display: flex;
        align-items: flex-start;
        justify-content: center;
        filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));
      ">${pinSvg}</div>
    `,
    className: 'facility-marker',
    iconSize: [iconWidth, iconHeight],
    iconAnchor: [iconWidth / 2, iconHeight],
    popupAnchor: [0, -iconHeight]
  });
};

const WildfireMarker = ({ fire, alertLevel }) => {
  const popupRef = useRef();
  
  if (!fire || !fire.latitude || !fire.longitude) {
    return null;
  }

  // Parse perimeter data if available (now from NIFC via R script)
  let perimeters = [];
  if (fire.has_perimeter && fire.perimeter_geojson) {
    try {
      // Safely parse the GeoJSON data with multiple fallbacks
      let geojson = null;

      if (typeof fire.perimeter_geojson === 'string') {
        // Handle potentially double-encoded JSON
        try {
          let parsed = JSON.parse(fire.perimeter_geojson);
          // Check if we need to parse again (double-encoded)
          if (typeof parsed === 'string') {
            try {
              geojson = JSON.parse(parsed);
            } catch (innerErr) {
              console.warn(`Failed to parse double-encoded GeoJSON for ${fire.fire_name}:`, innerErr.message);
              geojson = null;
            }
          } else {
            geojson = parsed;
          }
        } catch (parseErr) {
          console.warn(`Failed to parse GeoJSON string for ${fire.fire_name}:`, parseErr.message);
          geojson = null;
        }
      } else if (typeof fire.perimeter_geojson === 'object' && fire.perimeter_geojson !== null) {
        // Already an object
        geojson = fire.perimeter_geojson;
      }

      // Validate we have a usable GeoJSON object
      if (geojson && typeof geojson === 'object' && geojson.type && Array.isArray(geojson.coordinates)) {
        // Validate coordinate bounds helper
        const isValidCoord = (coord) => {
          return Array.isArray(coord) &&
            coord.length >= 2 &&
            typeof coord[0] === 'number' &&
            typeof coord[1] === 'number' &&
            coord[0] >= -180 && coord[0] <= 180 &&  // longitude
            coord[1] >= -90 && coord[1] <= 90;      // latitude
        };

        if (geojson.type === 'Polygon') {
          // Single polygon - coordinates[0] is the outer ring
          const coords = geojson.coordinates[0];
          if (Array.isArray(coords) && coords.length >= 3) {
            // Filter out invalid coordinates and convert [lon, lat] to [lat, lon] for Leaflet
            const leafletCoords = coords
              .filter(isValidCoord)
              .map(coord => [coord[1], coord[0]]);

            if (leafletCoords.length >= 3) {
              perimeters.push(leafletCoords);
            }
          }
        } else if (geojson.type === 'MultiPolygon') {
          // Multiple polygons - each polygon in coordinates array
          geojson.coordinates.forEach((polygon, polyIdx) => {
            if (!Array.isArray(polygon) || polygon.length === 0) return;

            const outerRing = polygon[0]; // First ring is outer boundary
            if (Array.isArray(outerRing) && outerRing.length >= 3) {
              const leafletCoords = outerRing
                .filter(isValidCoord)
                .map(coord => [coord[1], coord[0]]);

              if (leafletCoords.length >= 3) {
                perimeters.push(leafletCoords);
              }
            }
          });
        }
      }
    } catch (e) {
      console.warn(`Failed to process fire perimeter for ${fire.fire_name}:`, e.message);
    }
  }

  const formatAcres = (acres) => {
    if (!acres) return 'Unknown';
    return `${Math.round(acres).toLocaleString()} acres`;
  };

  const formatDistance = (miles) => {
    if (!miles) return 'Unknown';
    return `${miles.toFixed(1)} mi`;
  };

  const formatContainment = (percent) => {
    if (percent === null || percent === undefined) return 'Unknown';
    return `${percent}%`;
  };

  const formatAge = (startDate) => {
    if (!startDate) return 'Unknown';
    const start = new Date(startDate);
    const now = new Date();
    const diffTime = Math.abs(now - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
  };

  const getAlertColor = (level) => {
    const colors = {
      critical: '#dc2626',  // Red
      warning: '#ea580c',   // Orange
      monitor: '#eab308'    // Yellow
    };
    return colors[level] || '#eab308';
  };

  return (
    <>
      {/* Fire perimeters - render all polygons if available */}
      {perimeters.map((perimeter, idx) => {
        return (
          <Polygon
            key={`perimeter-${fire.fire_id}-${idx}`}
            positions={perimeter}
            pathOptions={{
              color: getAlertColor(alertLevel),
              fillColor: getAlertColor(alertLevel),
              fillOpacity: 0.15,
              weight: 2.5,
              opacity: 0.9,
              dashArray: '5, 5'
            }}
            eventHandlers={{
              click: (e) => {
                // Close all other popups first
                e.target._map.eachLayer((layer) => {
                  if (layer.closePopup) {
                    layer.closePopup();
                  }
                });
                // Open this popup
                if (popupRef.current) {
                  popupRef.current._source.openPopup();
                }
              }
            }}
          />
        );
      })}

      {/* Fire location marker - always show */}
      <Marker
        position={[fire.latitude, fire.longitude]}
        icon={getFireIcon(alertLevel)}
        eventHandlers={{
          click: (e) => {
            // Close all other popups first
            e.target._map.eachLayer((layer) => {
              if (layer.closePopup) {
                layer.closePopup();
              }
            });
            // Open this popup
            if (popupRef.current) {
              popupRef.current._source.openPopup();
            }
          }
        }}
      >
        <Popup
          ref={popupRef}
          className="wildfire-popup"
          maxWidth={300}
          closeButton={false}
        >
          <div style={{ 
            minWidth: '200px',
            background: 'rgba(255, 255, 255, 0.95)',
            border: `2px solid ${getAlertColor(alertLevel)}`,
            borderRadius: '8px',
            padding: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            position: 'relative'
          }}>
            <button
              onClick={() => {
                if (popupRef.current) {
                  popupRef.current._source.closePopup();
                }
              }}
              style={{
                position: 'absolute',
                top: '4px',
                right: '4px',
                width: '20px',
                height: '20px',
                border: 'none',
                background: getAlertColor(alertLevel),
                color: 'white',
                borderRadius: '50%',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#333';
              }}
              onMouseOut={(e) => {
                e.target.style.background = getAlertColor(alertLevel);
              }}
            >
              ×
            </button>
            <h3 style={{ 
              margin: '0 0 6px 0', 
              color: getAlertColor(alertLevel),
              fontSize: '16px',
              fontWeight: 'bold',
              textAlign: 'center',
              paddingRight: '24px'
            }}>
              {fire.fire_name || 'Unnamed Fire'}
            </h3>
            
            <div style={{ fontSize: '13px', lineHeight: '1.3' }}>
              <p style={{ margin: '3px 0' }}><strong>Distance:</strong> {formatDistance(fire.distance_miles)}</p>
              <p style={{ margin: '3px 0' }}><strong>Size:</strong> {formatAcres(fire.area_acres)}</p>
              <p style={{ margin: '3px 0' }}><strong>Containment:</strong> {formatContainment(fire.containment_percent)}</p>

              {fire.cause && (
                <p style={{ margin: '3px 0' }}><strong>Cause:</strong> {fire.cause}</p>
              )}

              {fire.start_date && (
                <p style={{ margin: '3px 0' }}><strong>Age:</strong> {formatAge(fire.start_date)}</p>
              )}
              
              <div style={{ 
                marginTop: '6px',
                padding: '3px 6px',
                backgroundColor: getAlertColor(alertLevel),
                color: 'white',
                borderRadius: '4px',
                textAlign: 'center',
                fontSize: '11px',
                fontWeight: 'bold',
                textTransform: 'uppercase'
              }}>
                {alertLevel}
              </div>

              {perimeters.length > 0 && (
                <p style={{
                  fontSize: '11px',
                  color: '#666',
                  margin: '3px 0 0 0',
                  fontStyle: 'italic',
                  textAlign: 'center'
                }}>
                  ✓ Perimeter boundary displayed
                </p>
              )}
            </div>
          </div>
        </Popup>
      </Marker>
    </>
  );
};

export default WildfireMarker;
